import React, { useState, useCallback } from 'react';
import { MarkerF, InfoWindowF } from '@react-google-maps/api';
import { Move, Trash2, Edit2, View as StreetView, Navigation, Map, AlertCircle } from 'lucide-react';
import { POI_TYPES } from '../../../../constants/poiTypes';
import { logInfo } from '../../../../utils/logger';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../../config/firebase';
import { useNavigate } from 'react-router-dom';
import { queueEmail } from '../../../../services/emailService';
import { useSurvey } from '../../../../contexts/SurveyContext';

export const MapMarker = ({ 
  poi, 
  clusterer, 
  isSelected = false,
  onEdit,
  onDelete,
  map,
  onDriveTo,
  size = 32,
  zIndex = 1
}) => {
  const [showInfo, setShowInfo] = useState(isSelected);
  const [isMoving, setIsMoving] = useState(false);
  const [toast, setToast] = useState(null);
  const [infoContent, setInfoContent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const navigate = useNavigate();
  const { activeSurvey } = useSurvey();
  const poiType = POI_TYPES[poi.type];

  const handleClick = useCallback(() => {
    setShowInfo(true);
    
    // Handle profile POI click
    if (poi.type === 'Profile' && poi.profileId) {
      const loadProfile = async () => {
        try {
          const profileRef = doc(db, 'road_profiles', poi.profileId);
          const profileDoc = await getDoc(profileRef);
          if (profileDoc.exists()) {
            const profileData = profileDoc.data();
            setInfoContent({
              type: 'profile',
              data: profileData,
              actions: [
                {
                  label: 'View Profile',
                  onClick: () => navigate('/roadscope/profile', { 
                    state: { profileId: poi.profileId } 
                  })
                },
                {
                  label: 'Send by Email',
                  onClick: async () => {
                    const email = prompt('Enter email address:');
                    if (email) {
                      try {
                        await queueEmail(email, 'Road Profile Report', {
                          template: 'ROAD_PROFILE',
                          variables: {
                            profileData,
                            surveyName: activeSurvey?.name
                          }
                        });
                        setToast({
                          type: 'success',
                          message: 'Profile sent successfully'
                        });
                      } catch (error) {
                        setToast({
                          type: 'error',
                          message: 'Failed to send profile'
                        });
                      }
                    }
                  }
                }
              ]
            });
          }
        } catch (error) {
          logError('Failed to load profile:', error);
        }
      };
      loadProfile();
    }
    
    // Handle profile POI click
    if (poi.type === 'Profile' && poi.profileId) {
      const loadProfile = async () => {
        try {
          const profileRef = doc(db, 'road_profiles', poi.profileId);
          const profileDoc = await getDoc(profileRef);
          if (profileDoc.exists()) {
            const profileData = profileDoc.data();
            setShowInfo(true);
            setInfoContent({
              type: 'profile',
              data: profileData,
              actions: [
                {
                  label: 'View Profile',
                  onClick: () => navigate('/roadscope/profile', { 
                    state: { profileId: poi.profileId } 
                  })
                },
                {
                  label: 'Send by Email',
                  onClick: async () => {
                    const email = prompt('Enter email address:');
                    if (email) {
                      try {
                        await queueEmail(email, 'Road Profile Report', {
                          template: 'ROAD_PROFILE',
                          variables: {
                            profileData,
                            surveyName: activeSurvey?.name
                          }
                        });
                        setToast({
                          type: 'success',
                          message: 'Profile sent successfully'
                        });
                      } catch (error) {
                        setToast({
                          type: 'error',
                          message: 'Failed to send profile'
                        });
                      }
                    }
                  }
                }
              ]
            });
          }
        } catch (error) {
          logError('Failed to load profile:', error);
        }
      };
      loadProfile();
    }
    logInfo(`POI marker clicked: ${poi.id}`);
  }, [poi.id]);

  const handleCloseInfo = useCallback(() => {
    setShowInfo(false);
  }, []);

  const handleMoveStart = useCallback(() => {
    setIsMoving(true);
    setShowInfo(false);
  }, []);

  const handleMoveEnd = useCallback(async (e) => {
    const newLat = e.latLng.lat();
    const newLng = e.latLng.lng();
    
    try {
      await updateDoc(doc(db, 'pois', poi.id), {
        coordinates: {
          latitude: newLat,
          longitude: newLng
        },
        updatedAt: new Date().toISOString()
      });
      logInfo('POI position updated successfully');
    } catch (error) {
      console.error('Failed to update POI position:', error);
      alert('Failed to save new position');
    }
    
    setIsMoving(false);
    setShowInfo(true);
  }, [poi.id]);

  const handleViewOnGoogleMaps = useCallback(() => {
    const url = `https://www.google.com/maps?q=${poi.coordinates.latitude},${poi.coordinates.longitude}`;
    window.open(url, '_blank');
  }, [poi.coordinates]);

  const handleStreetView = useCallback(() => {
    if (!map) return;
    
    const panorama = map.getStreetView();
    panorama.setPosition({
      lat: Number(poi.coordinates.latitude),
      lng: Number(poi.coordinates.longitude)
    });
    panorama.setVisible(true);
  }, [map, poi.coordinates]);

  const handleDriveTo = useCallback(() => {
    if (!map) return;
    
    // Get destination coordinates
    const destination = {
      lat: Number(poi.coordinates.latitude),
      lng: Number(poi.coordinates.longitude)
    };

    // Call parent handler to set up directions
    onDriveTo(destination);
    
    // Close info window
    setShowInfo(false);
  }, [map, poi.coordinates, onDriveTo]);

  const getIcon = useCallback(() => {
    const Icon = poiType?.icon;
    if (!Icon) return null;

    const svg = `
      <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="${poiType.color}" stroke="white" stroke-width="2"/>
        <path d="${Icon}" fill="white"/>
      </svg>
    `;

    return {
      url: `data:image/svg+xml;base64,${btoa(svg)}`,
      scaledSize: new google.maps.Size(size, size),
      anchor: new google.maps.Point(size/2, size/2)
    };
  }, [poiType, size]);

  const confirmDelete = () => {
    onDelete(poi.id);
    setShowDeleteConfirm(false);
    setShowInfo(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <MarkerF
        position={{
          lat: Number(poi.coordinates.latitude),
          lng: Number(poi.coordinates.longitude)
        }}
        clusterer={clusterer}
        draggable={isMoving}
        icon={getIcon()}
        onClick={handleClick}
        onDragStart={handleMoveStart}
        onDragEnd={handleMoveEnd}
        zIndex={zIndex}
      />

      {showInfo && (
        <InfoWindowF
          position={{
            lat: Number(poi.coordinates.latitude),
            lng: Number(poi.coordinates.longitude)
          }}
          onCloseClick={handleCloseInfo}
          options={{
            pixelOffset: new google.maps.Size(0, -40)
          }}
        >
          <div className="bg-white p-3 rounded-lg min-w-[200px]">
            {/* Delete Confirmation Overlay */}
            {showDeleteConfirm && (
              <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
                <div className="bg-white p-4 rounded-lg shadow-lg max-w-[200px] text-center">
                  <div className="flex justify-center mb-2">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <p className="text-gray-800 font-medium mb-3">Delete this POI?</p>
                  <div className="flex justify-center gap-2">
                    <button 
                      onClick={cancelDelete}
                      className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={confirmDelete}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* POI Image */}
            {poi.photos?.[0] && (
              <img 
                src={poi.photos[0]} 
                alt="POI"
                className="w-full h-32 object-cover rounded-lg mb-2"
              />
            )}

            {/* Header */}
            <div className="flex items-center gap-2 mb-2">
              {poiType?.icon && (
                <div 
                  className="p-1.5 rounded"
                  style={{ backgroundColor: `${poiType.color}20` }}
                >
                  {React.createElement(poiType.icon, { 
                    className: "w-4 h-4",
                    style: { color: poiType.color }
                  })}
                </div>
              )}
              <div>
                <h3 className="font-medium text-sm">
                  {poiType?.prefix || 'POI'}-{poi.id}
                </h3>
                <p className="text-xs text-gray-500">
                  {poi.coordinates.latitude.toFixed(6)}, {poi.coordinates.longitude.toFixed(6)}
                </p>
              </div>
            </div>

            {/* Notes */}
            {poi.notes && (
              <p className="text-xs text-gray-600 mb-2">{poi.notes}</p>
            )}

            {/* Actions */}
            <div className="grid grid-cols-3 gap-1 text-xs mb-1">
              <button
                onClick={handleMoveStart}
                className="flex flex-col items-center p-1 text-gray-600 hover:bg-gray-100 rounded"
              >
                <Move className="w-4 h-4" />
                <span>Move</span>
              </button>

              <button
                onClick={() => onEdit(poi)}
                className="flex flex-col items-center p-1 text-gray-600 hover:bg-gray-100 rounded"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit</span>
              </button>

              <button
                onClick={() => {
                  setShowDeleteConfirm(true);
                  setShowInfo(true);
                }}
                className="flex flex-col items-center p-1 text-red-600 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>

              <button
                onClick={handleStreetView}
                className="flex flex-col items-center p-1 text-gray-600 hover:bg-gray-100 rounded"
              >
                <StreetView className="w-4 h-4" />
                <span>Street</span>
              </button>

              <button
                onClick={handleDriveTo}
                className="flex flex-col items-center p-1 text-gray-600 hover:bg-gray-100 rounded"
              >
                <Navigation className="w-4 h-4" />
                <span>Drive</span>
              </button>

              <button
                onClick={handleViewOnGoogleMaps}
                className="flex flex-col items-center p-1 text-gray-600 hover:bg-gray-100 rounded"
              >
                <Map className="w-4 h-4" />
                <span>Google</span>
              </button>
            </div>
          </div>
        </InfoWindowF>
      )}
    </>
  );
};

export default MapMarker;